/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { MessageService } from '@core/lib/services/message.service';
import { UtilService } from '@app/util/util.service';
import { appConstants } from '@app/common/app-constants';
import { ModalService } from '@core/lib/services/modal.service';
import { ModalRef } from '@core/lib/components/modal/modal-ref';
import { ArchiveAdoptedModalComponent } from '@app/common/modals/components/archive-adopted/archive-adopted-modal.component';
import { AppConfigService } from '@app/common/app-config.service';
import { StudyDataParam } from './bring-online.types';
import { TruePacsBringOnline } from '@app/common/services/bring-online/true-pacs-bring-online';
import { PacsBringOnline } from '@app/common/services/bring-online/pacs-bring-online';

const log = new Logger('ArchiveAdoptedService');

class TruePacs extends TruePacsBringOnline {
  constructor() {
    super();
  }
}

class Pacs extends PacsBringOnline {
  constructor() {
    super();
  }
}

@Injectable({
  providedIn: 'root'
})
export class ArchiveAdoptedService {
  private archiveAdoptedModalRef: ModalRef;
  private bringOnline: TruePacs | Pacs;
  private cpacsVersion: string;

  ArchiveAdoptionStatus = appConstants.archiveAdoptionStatus;
  studyDataAvailability = appConstants.studyDataAvailability;

  constructor(
    private utilService: UtilService,
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private translate: TranslateService,
    private modalService: ModalService,
    private appConfigService: AppConfigService
  ) {}

  init() {
    this.cpacsVersion = this.appConfigService.getAppConfigByKey('cpacsVersion');

    if (this.cpacsVersion === 'v8') {
      this.bringOnline = new TruePacs();
    } else {
      this.bringOnline = new Pacs();
    }
  }

  startFetch(examCkey, repositoryType, aeTitle) {
    log.info('archiveAdoptedService - fetchArchivedExam(), examCkey -' + examCkey);
    return new Promise((resolve, reject) => {
      let uri;
      if (!UtilService.isRemoteExam({ repositoryType })) {
        uri = `requested-procedures/fetchArchiveAdoptedExam?id=${examCkey}`;
      } else {
        uri = `requested-procedures/fetchArchiveAdoptedExam?id=${examCkey}&repositoryType=${repositoryType}&aeTitle=${aeTitle}`;
      }
      if (this.bringOnline.inProgress.indexOf(examCkey) < 0) {
        this.bringOnline.inProgress.push(examCkey);
      }
      this.updateBringOnlineStatus(examCkey, appConstants.archiveAdoptionStatus.InProgress);
      this.dataService.get(uri).then(
        data => {
          log.info('Integration AA fetch passed' + data);
          resolve(undefined);
        },
        reason => {
          log.error(' Integration - get AA fetch failed  - id - ' + examCkey + ', Response- ' + JSON.stringify(reason));
          if (this.bringOnline.inProgress.indexOf(examCkey) >= 0) {
            this.bringOnline.inProgress.splice(this.bringOnline.inProgress.indexOf(examCkey), 1);
          }
          if (this.bringOnline.error.indexOf(examCkey) < 0) {
            this.bringOnline.error.push(examCkey);
          }
          this.updateBringOnlineStatus(examCkey, appConstants.archiveAdoptionStatus.Error);
          reject(reason);
        }
      );
    });
  }

  update(data: any[]) {
    const archiveStatuses: any[] = this.bringOnline.updateStatuses(data);
    if (archiveStatuses.length > 0) {
      this.updateViewImageButtonAndImageCount(archiveStatuses);
    }
  }

  updateBringOnlineStatus(id: number, status) {
    this.messageService.broadcast('startFetchStatus', {
      id: id,
      status: status
    });
  }

  getArchiveFetchStatus(id: any, studyDataParam: StudyDataParam) {
    return this.bringOnline.getBringOnlineStatus(id, studyDataParam);
  }

  isOfflineExam(studyDataParam: StudyDataParam | any) {
    return this.bringOnline.isOfflineExam(studyDataParam);
  }

  isExamAvailable(workItem: any) {
    return this.bringOnline.isExamAvailable(workItem);
  }

  openArchivedWorkItemPopup(event, workItem, examCkey) {
    const popupParams: any = this.bringOnline.getPopupParams(workItem);
    if (popupParams) {
      this.showArchivedWorkItemPopup(popupParams.isDownloadable, workItem, examCkey, popupParams.isZeroImageExam);
    }
  }

  closeArchivedWorkItemPopup() {
    if (this.archiveAdoptedModalRef) {
      this.archiveAdoptedModalRef.close();
    }
  }

  getRemoteOrArchiveIndicator(listItem: any) {
    return this.getArchiveFetchStatus(listItem.id, UtilService.fetchStudyDataParam(listItem));
  }

  private showArchivedWorkItemPopup(isDownloadable, workItem, examCkey, zeroImageExam) {
    log.info(
      'archiveAdoptedService - showArchivedWorkItemPopup(), isDownloadable -' +
        isDownloadable +
        ', workItem - ' +
        workItem +
        ', examCkey - ' +
        examCkey
    );

    this.archiveAdoptedModalRef = this.modalService.openModal(ArchiveAdoptedModalComponent, {
      headerContent: isDownloadable
        ? this.translate.instant('workloadManager.listContainer.worklist.bringExamOnlineHeader')
        : this.translate.instant('workloadManager.listContainer.worklist.cannotBringExamOnlineHeader'),
      data: {
        isDownloadable,
        viewImagesButtonEnabled: this.isViewImagesButtonEnabled(workItem),
        showViewImagesButton:
          UtilService.isArchivedExam(workItem.archiveAdoptedStatus) &&
          workItem.studyDataAvailability === appConstants.studyDataAvailability.ONLINE,
        bringOnlineButtonEnabled: isDownloadable ? zeroImageExam : undefined
      },
      modalClass: 'modal--medium'
    });
    const sub = this.archiveAdoptedModalRef.afterClosed.subscribe(data => {
      if (data === appConstants.YES) {
        this.startFetch(examCkey, workItem.repositoryType, workItem.aeTitle);
      } else if (data === appConstants.SHOW_IMAGES) {
        this.messageService.broadcast('archivedViewImages', { launchOfflinePopup: false });
      }
      sub.unsubscribe();
    });
  }

  getArchiveIconTooltip(item: any): string {
    let tooltip;
    switch (item.archiveFetchStatus) {
      case appConstants.archiveAdoptionStatus.InProgress: {
        tooltip = this.translate.instant('icons.tooltip.bringingInProgress');
        break;
      }
      case appConstants.studyDataAvailability.NEARLINE: {
        tooltip = this.translate.instant('icons.tooltip.nearline');
        break;
      }
      case appConstants.studyDataAvailability.OFFLINE: {
        if (this.cpacsVersion === 'v8') {
          tooltip = this.translate.instant('icons.tooltip.offlineAvailableImages');
        } else {
          tooltip = this.translate.instant('icons.tooltip.downloadUnavailable');
        }
        break;
      }
      case appConstants.archiveAdoptionStatus.Error: {
        tooltip = this.translate.instant('icons.tooltip.bringOnline.error');
        break;
      }
      case appConstants.studyDataAvailability.THIRD_PARTY: {
        tooltip =
          item.imageCount === 0
            ? this.translate.instant('icons.tooltip.downloadUnavailable')
            : this.translate.instant('icons.tooltip.offlineAvailableImages');
        break;
      }
    }
    return tooltip;
  }

  getArchiveIcon(item: any): string {
    let iconName;
    switch (item.archiveFetchStatus) {
      case appConstants.archiveAdoptionStatus.InProgress: {
        iconName = 'ico-spinner-16';
        break;
      }
      case appConstants.studyDataAvailability.NEARLINE: {
        iconName = 'ico-clock-16';
        break;
      }
      case appConstants.studyDataAvailability.OFFLINE: {
        if (this.cpacsVersion === 'v8') {
          iconName = 'ico-download-16';
        } else {
          iconName = 'ico-download-unavailable-16';
        }
        break;
      }
      case appConstants.archiveAdoptionStatus.Error: {
        iconName = 'ico-alert-16';
        break;
      }
      case appConstants.studyDataAvailability.THIRD_PARTY: {
        iconName = item.imageCount === 0 ? 'ico-download-unavailable-16' : 'ico-download-16';
        break;
      }
    }
    return iconName;
  }

  private isViewImageBtnDisabledOnArchiveFetchStatus(viewImagesInfo) {
    const archiveFetchStatus = this.getArchiveFetchStatus(
      viewImagesInfo.id,
      UtilService.fetchStudyDataParam(viewImagesInfo)
    );

    // For archive adopted exams, image count is not reliable and so, it should not be considered for disabling
    // View Images button
    const isNotArchiveAdopted = archiveFetchStatus === appConstants.archiveAdoptionStatus.NO;
    if (isNotArchiveAdopted) {
      return viewImagesInfo.imageCount === 0 || archiveFetchStatus === appConstants.studyDataAvailability.OFFLINE;
    }
    return (
      archiveFetchStatus === appConstants.studyDataAvailability.OFFLINE ||
      archiveFetchStatus === appConstants.archiveAdoptionStatus.Error ||
      archiveFetchStatus === appConstants.archiveAdoptionStatus.InProgress
    );
  }

  private isViewImagesButtonEnabled(viewImagesInfo) {
    const flag = this.isViewImageBtnDisabledOnArchiveFetchStatus(viewImagesInfo);
    if (!_.isUndefined(flag)) {
      return flag;
    }
  }

  private updateViewImageButtonAndImageCount(archiveStatuses) {
    this.messageService.broadcast('ImagingHistoryAAStatusUpdate', archiveStatuses);
    this.updateTabContext(archiveStatuses);
  }

  private updateTabContext(archiveStatuses) {
    if (archiveStatuses) {
      archiveStatuses.forEach(exam => {
        const tab = this.utilService.getTab(exam.examCkey);
        if (tab) {
          // tab.workItem.keys.imageCount = exam.imageCount;
          tab.workItem.keys.archiveAdoptedStatus = exam.newStatus;
          if (exam.newStatus === appConstants.archiveAdoptionStatus.NO) {
            tab.workItem.keys.studyDataAvailability = appConstants.studyDataAvailability.ONLINE;
            tab.workItem.keys.archiveAdopted = false;
          }
        }
      });
    }
  }
}
